/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * CourseHeader
 *
 * @file CourseHeader.jsx
 * @author Gladson Samuel S
 * @since 1.0.0
 */

import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Menu, Typography } from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { BUTTON_TYPES, POSITIONS } from '../../common/constants';
import Icon from '../icons/Icon';
import {
  CourseHeaderCourseCard,
  CourseHeaderCourseCardTwoColumns,
  CourseHeaderCourseCardContent,
  CourseHeaderCourseTitle,
  CourseHeaderInstructorContainer,
  CourseHeaderAvatar,
  CourseHeaderAvatarImage,
  CourseHeaderInstructorName,
  CourseHeaderIconButton,
  CourseHeaderHotspot,
  CourseHeaderSecondaryButton,
  CourseHeaderActionsContainer,
  CourseHeaderScheduleContainer,
  CourseHeaderScheduleTitle,
  CourseHeaderScheduleItem,
  CourseHeaderScheduleItemTitle,
  CourseHeaderScheduleItemsContainer,
  CourseHeaderScheduleItemTime,
  CourseHeaderMenuPaper,
  CourseHeaderMenuList,
  CourseHeaderMenuItem,
  CourseHeaderIconCtaButton,
  CourseHeaderIcon,
  CourseHeaderCourseMenuIcon,
  CourseHeaderMetaDataContainer,
  CourseHeaderMetaDataItem,
  CourseHeaderMetaDataLabel,
  CourseHeaderMetaDataValue,
  CourseHeaderInviteStudentsButton,
  CourseHeaderFooterContainer,
  CourseHeaderCourseTitleContainer,
  CourseHeaderActionContainer,
  CourseHeaderCourseChildrenContent,
  CourseHeaderButton,
  CourseHeaderInfoContainer
} from './styles/CourseHeaderStyles';

const CourseCard = (props) => {
  const {
    course = {},
    config = {},
    callback = {},
    children = null
  } = props;

  const {
    showClassSchedule = true,
    showCourseActions = true,
    showInstructor = true,
    showCourseTitle = true,
    showCourseMenu = true,
    showCourseHeaderActions = true,
    showInTwoColumns = false,
    showMetaData = true,
    showInviteStudents = true
  } = config;

  const { handleMenuOpenChange } = callback;

  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
    handleMenuOpenChange?.();
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const renderTitle = () => {
    if (!showCourseTitle || !course.title) {
      return null;
    }

    return (
      <CourseHeaderCourseTitle>
        {course.title}
      </CourseHeaderCourseTitle>
    );
  };

  const renderInstructor = () => {
    if (!showInstructor || !course.instructor) {
      return null;
    }

    return (
      <CourseHeaderInstructorContainer>
        {(() => {
          if (course.instructorAvatarUrl) {
            return <CourseHeaderAvatarImage src={course.instructorAvatarUrl} alt="Instructor avatar" />;
          }

          if (course.instructorAvatar) {
            return (
              <CourseHeaderAvatar>
                <span>{course.instructorAvatar}</span>
              </CourseHeaderAvatar>
            );
          }

          return (
            <CourseHeaderAvatar>
              <AccountCircleIcon />
            </CourseHeaderAvatar>
          );
        })()}

        <CourseHeaderInstructorName>
          {course.instructor}
        </CourseHeaderInstructorName>
      </CourseHeaderInstructorContainer>
    );
  };

  const renderIconButton = (action) => {
    if (!action.icon && !action.iconName) {
      return null;
    }

    if (action.iconName) {
      return (
        <CourseHeaderIconButton onClick={action.callback}>
          {action.showHotspot && <CourseHeaderHotspot />}
          <Icon name={action.iconName} />
        </CourseHeaderIconButton>
      );
    }

    return (
      <CourseHeaderIconButton onClick={action.callback}>
        {action.showHotspot && <CourseHeaderHotspot />}
        {action.icon}
      </CourseHeaderIconButton>
    );
  };

  const renderTextButton = (action) => {
    if (!action.text) {
      return null;
    }

    return (
      <CourseHeaderSecondaryButton
        onClick={action.callback}
      >
        {action.text}
      </CourseHeaderSecondaryButton>
    );
  };


  const renderAction = (action) => {
    if (!action.callback) {
      return null;
    }

    if (action.type === BUTTON_TYPES.ICON) {
      return renderIconButton(action);
    }

    if (action.type === BUTTON_TYPES.BUTTON) {
      return renderTextButton(action);
    }

    return null;
  };

  const renderActions = (position = POSITIONS.BOTTOM) => {
    if (!showCourseActions || !course?.actions?.length) {
      return null;
    }

    const actions = course.actions.filter((action) => action.position === position);

    return actions.length ? (
      <CourseHeaderActionsContainer>
        {actions.map((action) => renderAction(action))}
      </CourseHeaderActionsContainer>
    ) : null;
  };

  const renderScheduleItem = (schedule) => {
    if (!schedule) {
      return null;
    }

    return (
      <CourseHeaderScheduleItem>
        {schedule.label && <CourseHeaderScheduleItemTitle>{schedule.label}</CourseHeaderScheduleItemTitle>}
        {schedule.time && <CourseHeaderScheduleItemTime>{schedule.time}</CourseHeaderScheduleItemTime>}
      </CourseHeaderScheduleItem>
    );
  };

  const renderSchedule = () => {
    if (!showClassSchedule || !course.classSchedule) {
      return null;
    }

    return (
      <CourseHeaderScheduleContainer>
        {course?.classSchedule?.label
          && <CourseHeaderScheduleTitle>{course.classSchedule.label}</CourseHeaderScheduleTitle>
        }
        <CourseHeaderScheduleItemsContainer>
          {course?.classSchedule?.schedule?.map((schedule) => renderScheduleItem(schedule))}
        </CourseHeaderScheduleItemsContainer>
      </CourseHeaderScheduleContainer>
    );
  };

  const renderCourseMenu = () => {
    if (!course.courseMenu || !course.courseMenu.length) {
      return null;
    }

    return (
      <Menu
        id="course-menu"
        anchorEl={menuAnchorEl}
        keepMounted
        open={isMenuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        slots={{
          paper: CourseHeaderMenuPaper,
          list: CourseHeaderMenuList
        }}
      >
        {course.courseMenu.map((item) => (
          <CourseHeaderMenuItem
            key={item.label}
            disableRipple
            disableGutters
            hasDivider={item.divider}
            onClick={() => {
              handleMenuClose();
              item.callback(item);
            }}
          >
            <Typography>{item.label}</Typography>
          </CourseHeaderMenuItem>
        ))}
      </Menu>
    );
  };

  const renderCtaAction = (action) => {
    if (!action.callback) {
      return null;
    }

    if (action.iconName && action.text) {
      return (
        <CourseHeaderIconCtaButton
          onClick={() => action.callback(action)}
          startIcon={<CourseHeaderIcon name={action.iconName} />}
        >
          {action.text}
        </CourseHeaderIconCtaButton>
      );
    }

    return (
      <CourseHeaderButton onClick={() => action.callback(action)}>{action.text}</CourseHeaderButton>
    );
  };

  const renderCourseMenuIcon = () => {
    if (!showCourseMenu) {
      return null;
    }

    return (
      <Fragment>
        <CourseHeaderCourseMenuIcon
          isMenuOpen={isMenuOpen}
          aria-haspopup="true"
          aria-expanded={isMenuOpen ? 'true' : 'false'}
          aria-controls={isMenuOpen ? 'course-menu' : undefined}
          aria-label="Course menu"
          onClick={handleMenuOpen}
        >
          <Icon name="ThreeDotMenuIcon" />
        </CourseHeaderCourseMenuIcon>

        {renderCourseMenu()}
      </Fragment>
    );
  };

  const renderCourseHeaderActions = (position = POSITIONS.TOP) => {
    if (!showCourseHeaderActions || !course?.actions?.length) {
      return null;
    }
    const actions = course.actions.filter((action) => action.position === position);

    return actions.length ? actions.map((action) => renderCtaAction(action)) : null;
  };

  const renderMetaDataItem = (meta) => {
    if (!meta) {
      return null;
    }

    return (
      <CourseHeaderMetaDataItem>
        <CourseHeaderMetaDataLabel>{meta.label}</CourseHeaderMetaDataLabel>
        <CourseHeaderMetaDataValue>{meta.info}</CourseHeaderMetaDataValue>
      </CourseHeaderMetaDataItem>
    );
  };

  const renderMetaData = () => {
    if (!showMetaData || !course.metaData || !course.metaData.length) {
      return null;
    }

    return (
      <CourseHeaderMetaDataContainer>
        {course.metaData.map((meta) => renderMetaDataItem(meta))}
      </CourseHeaderMetaDataContainer>
    );
  };

  const renderInviteStudents = () => {
    if (!showInviteStudents || !course.inviteStudents) {
      return null;
    }

    return (
      <CourseHeaderInviteStudentsButton onClick={course.inviteStudents.callback}>
        {course.inviteStudents.text}
      </CourseHeaderInviteStudentsButton>
    );
  };

  if (showInTwoColumns) {
    return (
      <CourseHeaderCourseCardTwoColumns>
        <CourseHeaderCourseCardContent>
          {renderTitle()}
          {renderMetaData()}
          <CourseHeaderFooterContainer>
            {renderInviteStudents()}
            {renderCourseMenuIcon()}
          </CourseHeaderFooterContainer>
        </CourseHeaderCourseCardContent>
      </CourseHeaderCourseCardTwoColumns>
    );
  }

  return (
    <CourseHeaderCourseCard>
      <CourseHeaderCourseTitleContainer>
        <CourseHeaderActionContainer>
          {renderTitle()}
          {renderCourseHeaderActions()}
        </CourseHeaderActionContainer>
        {renderCourseMenuIcon()}
      </CourseHeaderCourseTitleContainer>

      {renderSchedule()}
      <CourseHeaderInfoContainer>
        {renderInstructor()}
        {renderActions()}
      </CourseHeaderInfoContainer>

      <CourseHeaderCourseChildrenContent>
        {children}
      </CourseHeaderCourseChildrenContent>
    </CourseHeaderCourseCard>
  );
};

CourseCard.propTypes = {
  course: PropTypes.object,
  config: PropTypes.object,
  callback: PropTypes.shape({
    handleMenuOpenChange: PropTypes.func
  }),
  children: PropTypes.node
};

CourseCard.defaultProps = {
  course: {},
  config: {},
  callback: {},
  children: null
};

export default CourseCard;
